import { useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, TrendingUp, PiggyBank, Landmark, Sparkles, ArrowUpRight, Camera, ArrowDownRight } from "lucide-react";
import { useFinancialState } from "@/context/StateContext";
import { computeMetrics, fmtMoney, fmtPct, fmtDate, uid, todayISO } from "@/lib/finance";
import { dayLabel, periodLabel } from "@/lib/wellness";
import { periodKey } from "@/lib/life";
import { usePeriod } from "@/context/PeriodContext";
import { PageHeader, KpiCard, SectionCard, Money, EmptyState, AddButton } from "@/components/shared";
import { PeriodNav } from "@/components/PeriodNav";
import { NetWorthChart, AllocationDonut } from "@/components/Charts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

export default function Overview() {
  const { state, update, loading } = useFinancialState();
  const { horizon, setHorizon, date, setDate } = usePeriod();
  if (loading || !state) return <PageSkeleton />;

  const m = computeMetrics(state);
  const history = (state.netWorthHistory || []).map((h) => ({ label: fmtDate(h.date).replace(/,.*/, ""), value: h.value }));
  const alloc = [
    { name: "Cash", value: Math.round(m.cash) },
    { name: "Investments", value: Math.round(m.investments) },
    { name: "Other assets", value: Math.round(m.assets) },
    { name: "Debt", value: Math.round(m.debt) },
  ].filter((x) => x.value > 0);

  // Period-aware cash flow + transactions for the selected Day/Week/Month/Quarter/Year (or custom date)
  const inPeriod = (t) => t.date && periodKey(horizon, t.date) === periodKey(horizon, date);
  const periodTx = (state.transactions || []).filter(inPeriod).slice().sort((a, b) => (a.date < b.date ? 1 : -1));
  const isFlow = (t) => t.type !== "transfer";
  const pIncome = periodTx.filter((t) => isFlow(t) && Number(t.amount) > 0).reduce((s, t) => s + Number(t.amount), 0);
  const pSpend = periodTx.filter((t) => isFlow(t) && Number(t.amount) < 0).reduce((s, t) => s + Math.abs(Number(t.amount)), 0);
  const pNet = pIncome - pSpend;
  const pLabel = periodLabel(horizon, date);
  const recent = periodTx.slice(0, 10);

  const captureSnapshot = () => {
    const today = todayISO();
    update((prev) => ({
      ...prev,
      netWorthHistory: [...(prev.netWorthHistory || []).filter((h) => h.date !== today), { date: today, value: Math.round(m.netWorth) }],
    }), "Captured net worth snapshot");
    toast.success("Net worth snapshot captured");
  };

  return (
    <div>
      <PageHeader
        title="Overview"
        subtitle={`Your complete financial picture · ${dayLabel()}`}
        testid="overview-header"
        actions={<AddButton onClick={captureSnapshot} variant="secondary" testid="capture-snapshot-button"><Camera className="h-4 w-4 mr-1.5" /> Capture snapshot</AddButton>}
      />

      <div className="mb-5 flex flex-col gap-3">
        <PeriodNav horizon={horizon} setHorizon={setHorizon} date={date} setDate={setDate} testid="overview-period" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="period-cashflow">
          <SectionCard testid="period-income">
            <div className="flex items-center justify-between"><span className="text-[13px] text-[var(--text-muted)]">Income · {pLabel}</span><ArrowDownRight className="h-4 w-4 text-[var(--green)]" /></div>
            <div className="font-serif-display text-2xl text-[var(--text)] tabular-nums mt-1">{fmtMoney(pIncome)}</div>
          </SectionCard>
          <SectionCard testid="period-spending">
            <div className="flex items-center justify-between"><span className="text-[13px] text-[var(--text-muted)]">Spending · {pLabel}</span><ArrowUpRight className="h-4 w-4 text-[var(--danger)]" /></div>
            <div className="font-serif-display text-2xl text-[var(--text)] tabular-nums mt-1">{fmtMoney(pSpend)}</div>
          </SectionCard>
          <SectionCard testid="period-net">
            <div className="flex items-center justify-between"><span className="text-[13px] text-[var(--text-muted)]">Net · {pLabel}</span><TrendingUp className="h-4 w-4 text-[var(--gold)]" /></div>
            <div className="font-serif-display text-2xl tabular-nums mt-1" style={{ color: pNet >= 0 ? "var(--green)" : "var(--danger)" }}>{pNet >= 0 ? "" : "-"}{fmtMoney(Math.abs(pNet))}</div>
          </SectionCard>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Net worth" value={fmtMoney(m.netWorth)} sub="Assets minus liabilities" icon={TrendingUp} accent testid="kpi-net-worth" />
        <KpiCard label="Available cash" value={fmtMoney(m.cash)} sub={m.txExpense ? `${(m.cash / (m.txExpense || 1)).toFixed(1)}x monthly spend` : "Liquid funds"} icon={Wallet} testid="kpi-cash" />
        <KpiCard label="Savings rate" value={fmtPct(m.savingsRate)} sub="Of monthly income" icon={PiggyBank} testid="kpi-savings-rate" />
        <KpiCard label="Investments" value={fmtMoney(m.investments)} sub="Invested assets" icon={Landmark} testid="kpi-investments" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-6">
        <div className="lg:col-span-8">
          <SectionCard title="Net worth trajectory" subtitle="Historical snapshots" testid="net-worth-card">
            {history.length ? <NetWorthChart data={history} /> : <EmptyState icon={TrendingUp} title="No snapshots yet" message="Capture a snapshot to start tracking your net worth over time." />}
          </SectionCard>
        </div>
        <div className="lg:col-span-4">
          <SectionCard title="Allocation" subtitle="Where your money lives" testid="allocation-card">
            {alloc.length ? <AllocationDonut data={alloc} /> : <EmptyState icon={Wallet} title="No accounts" message="Add accounts to see your allocation." />}
          </SectionCard>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8">
          <SectionCard title={`Transactions · ${pLabel}`} right={<Link to="/wealth/cash-flow" className="text-sm text-[var(--green)] hover:underline inline-flex items-center gap-1">View all <ArrowUpRight className="h-3.5 w-3.5" /></Link>} testid="recent-tx-card">
            {recent.length ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden sm:table-cell">Category</TableHead>
                    <TableHead className="hidden md:table-cell">Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recent.map((t) => (
                    <TableRow key={t.id} data-testid={`overview-tx-row-${t.id}`}>
                      <TableCell className="font-medium">{t.name}</TableCell>
                      <TableCell className="hidden sm:table-cell text-[var(--text-muted)]">{t.category}</TableCell>
                      <TableCell className="hidden md:table-cell text-[var(--text-muted)]">{fmtDate(t.date)}</TableCell>
                      <TableCell className="text-right"><Money value={t.amount} decimals={2} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : <EmptyState icon={Wallet} title="No transactions this period" message="Try a different date range, or add transactions in Cash flow." />}
          </SectionCard>
        </div>
        <div className="lg:col-span-4">
          <SectionCard title="AI wealth advisor" subtitle="Personalized guidance" testid="advisor-strip">
            <div className="flex flex-col items-start gap-3">
              <span className="grid place-items-center h-10 w-10 rounded-full bg-[color:rgba(198,139,60,0.14)] text-[var(--gold)]"><Sparkles className="h-5 w-5" /></span>
              <p className="text-sm text-[var(--text-muted)]">Get AI-generated strengths, risks, and next actions based on your full financial picture.</p>
              <Link to="/wealth/advisor"><AddButton testid="go-advisor-button">Open advisor</AddButton></Link>
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}

function PageSkeleton() {
  return (
    <div>
      <Skeleton className="h-10 w-56 mb-6" />
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28 rounded-[14px]" />)}
      </div>
      <Skeleton className="h-72 rounded-[14px]" />
    </div>
  );
}
